//TODO: Temp recreation until we are ready to pull in the cwan-ux packages
import BarChartIcon from '@mui/icons-material/BarChart'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import Box from '@mui/material/Box'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import { alpha, type Theme } from '@mui/material/styles'
import React, { useEffect, useMemo, useRef, useState } from 'react'

export type {
  TopNavigationBrandProps,
  TopNavigationFooterLinkProps,
  TopNavigationLinkProps,
  TopNavigationMenuProps,
  TopNavigationProps,
  TopNavigationSectionProps,
  TopNavigationUtilityActionProps
} from './TopNavigation.shared'

import {
  DownChevron,
  GearIcon,
  NavigationLink,
  RightChevron,
  TopNavigationBrandMarker,
  TopNavigationFooterLinkMarker,
  TopNavigationLinkMarker,
  TopNavigationMenuMarker,
  TopNavigationSectionMarker,
  getDefaultSectionKey,
  parseTopNavigationChildren,
  resolveOpenMenuSection,
  topNavigationStyles,
  type NavigationBrandSpec,
  type NavigationLeafSpec,
  type NavigationMenuSpec,
  type TopNavigationProps,
  type TopNavigationUtilityActionProps
} from './TopNavigation.shared'

interface TopNavigationBrandLinkProps {
  brand: NavigationBrandSpec
  hasTrailingRail: boolean
  linkComponent: TopNavigationProps['linkComponent']
  onNavigate: () => void
}

interface TopNavigationLeafItemProps {
  link: NavigationLeafSpec
  linkComponent: TopNavigationProps['linkComponent']
  onNavigate: () => void
}

interface TopNavigationMenuPanelProps {
  activeSectionKey: string | null
  linkComponent: TopNavigationProps['linkComponent']
  menu: NavigationMenuSpec
  onNavigate: () => void
  onSectionChange: (sectionKey: string) => void
}

interface TopNavigationMenuItemProps extends TopNavigationMenuPanelProps {
  anyMenuOpen: boolean
  isOpen: boolean
  onToggle: () => void
}

interface TopNavigationUtilityActionButtonProps {
  action: TopNavigationUtilityActionProps
  onAction: () => void
}

const getHoverBackground = (theme: Theme) =>
  (theme.palette.primary as { lightest?: string }).lightest ??
  theme.palette.action.hover

const getTopLevelItemSx = (isSelected: boolean) => (theme: Theme) => ({
  alignItems: 'center',
  backgroundColor: 'transparent',
  border: 0,
  borderRadius: '6px',
  color: isSelected ? theme.palette.primary.main : theme.palette.text.primary,
  cursor: 'pointer',
  display: 'inline-flex',
  fontFamily: theme.typography.fontFamily ?? 'Noto Sans',
  fontSize: '14px',
  gap: '3px',
  height: '44px',
  letterSpacing: '0.028px',
  lineHeight: 1,
  px: '16px',
  py: '5px',
  textAlign: 'center',
  transition: 'color 0.2s ease, background-color 0.2s ease',
  whiteSpace: 'nowrap',
  // Hover only shows the grey background; blue is reserved for the open/active menu.
  '&:hover': {
    backgroundColor: getHoverBackground(theme),
  },
})

// Plain top-level links (e.g. "Return to Factor Analytics") sit flush against the
// nav pill's edges, so a hover background can visually cover the thin blue shell
// border around it. Swap the grey hover fill for a text-color-only hover instead.
const getLeafItemSx = (isSelected: boolean) => (theme: Theme) => ({
  ...getTopLevelItemSx(isSelected)(theme),
  '&:hover': {
    backgroundColor: 'transparent',
    color: theme.palette.primary.main,
  },
})

interface TopNavigationRootLabelProps {
  isSelected: boolean
  label: React.ReactNode
}

const TopNavigationRootLabel = ({
  isSelected,
  label,
}: TopNavigationRootLabelProps): React.ReactElement => {
  const reservedLabelText =
    typeof label === 'string' || typeof label === 'number'
      ? String(label)
      : null

  return (
    <Box
      component="span"
      data-top-nav-label={reservedLabelText ?? undefined}
      sx={{
        alignItems: 'center',
        display: 'inline-grid',
        minWidth: 0,
        '&::before': reservedLabelText
          ? {
              content: 'attr(data-top-nav-label)',
              fontSize: '14px',
              fontWeight: 600,
              gridArea: '1 / 1',
              letterSpacing: '0.028px',
              lineHeight: 1,
              pointerEvents: 'none',
              visibility: 'hidden',
              whiteSpace: 'nowrap',
            }
          : undefined,
      }}
    >
      <Typography
        component="span"
        sx={{
          color: 'inherit',
          fontSize: '14px',
          fontWeight: isSelected ? 600 : 500,
          gridArea: '1 / 1',
          letterSpacing: '0.028px',
          lineHeight: 1,
          minWidth: 0,
          whiteSpace: 'nowrap',
        }}
      >
        {label}
      </Typography>
    </Box>
  )
}

const getIconButtonFocusRing = (outlineColor: string) => ({
  outline: `2px solid ${outlineColor}`,
  outlineOffset: '2px',
})

const TopNavigationBrandLink = ({
  brand,
  hasTrailingRail,
  linkComponent,
  onNavigate,
}: TopNavigationBrandLinkProps): React.ReactElement => (
  <NavigationLink
    linkComponent={linkComponent}
    onClick={onNavigate}
    sx={theme => ({
      alignItems: 'center',
      backgroundColor: theme.palette.primary.dark ?? theme.palette.primary.main,
      borderRadius: hasTrailingRail ? '6px 0 0 6px' : '6px',
      boxShadow: 'inset 0px 4px 4px 0px rgba(0, 0, 0, 0.25)',
      color: topNavigationStyles.brandTextColor,
      display: 'inline-flex',
      gap: '8px',
      height: '44px',
      px: '16px',
      whiteSpace: 'nowrap',
    })}
    to={brand.to}
  >
    <Box
      aria-hidden
      sx={{
        alignItems: 'center',
        color: topNavigationStyles.brandTextColor,
        display: 'inline-flex',
        fontSize: '16px',
        height: '16px',
        justifyContent: 'center',
        width: '16px',
      }}
    >
      <BarChartIcon sx={{ fontSize: 16 }} />
    </Box>
    <Typography
      component="span"
      sx={{
        color: topNavigationStyles.brandTextColor,
        fontSize: '16px',
        fontWeight: 600,
        letterSpacing: 0,
        lineHeight: 1,
        whiteSpace: 'nowrap',
      }}
    >
      {brand.label}
    </Typography>
  </NavigationLink>
)

const TopNavigationLeafItem = ({
  link,
  linkComponent,
  onNavigate,
}: TopNavigationLeafItemProps): React.ReactElement => (
  <Box
    component="li"
    sx={{
      alignItems: 'center',
      display: 'flex',
      listStyle: 'none',
    }}
  >
    <NavigationLink
      isActive={link.isActive}
      linkComponent={linkComponent}
      onClick={onNavigate}
      rel={link.rel}
      sx={getLeafItemSx(link.isActive)}
      target={link.target}
      to={link.to}
    >
      <TopNavigationRootLabel isSelected={link.isActive} label={link.label} />
    </NavigationLink>
  </Box>
)

const TopNavigationUtilityActionButton = ({
  action,
  onAction,
}: TopNavigationUtilityActionButtonProps): React.ReactElement => {
  const handleClick = () => {
    onAction()
    action.onClick?.()
  }

  return (
    <Box
      aria-label={action.ariaLabel}
      component="button"
      onClick={handleClick}
      sx={theme => ({
        alignItems: 'center',
        backgroundColor: 'transparent',
        border: 0,
        borderRadius: '6px',
        color: topNavigationStyles.utilityActionColor,
        cursor: 'pointer',
        display: 'inline-flex',
        height: '34px',
        justifyContent: 'center',
        px: '12px',
        py: '8px',
        transition: 'color 0.2s ease, background-color 0.2s ease',
        '&:hover': {
          backgroundColor:
            getHoverBackground(theme),
          color: theme.palette.primary.main,
        },
        '&:focus-visible': getIconButtonFocusRing(theme.palette.primary.main),
      })}
      type="button"
    >
      <Box
        aria-hidden
        sx={{
          alignItems: 'center',
          display: 'inline-flex',
          fontSize: '16px',
          height: '16px',
          justifyContent: 'center',
          width: '16px',
        }}
      >
        {action.icon}
      </Box>
    </Box>
  )
}

const TopNavigationMenuPanel = ({
  activeSectionKey,
  linkComponent,
  menu,
  onNavigate,
  onSectionChange,
}: TopNavigationMenuPanelProps): React.ReactElement => {
  const activeSection = resolveOpenMenuSection(menu, activeSectionKey)

  return (
    <Box
      sx={theme => ({
        alignItems: 'stretch',
        backgroundColor: theme.palette.background.paper,
        border: `1px solid ${topNavigationStyles.borderColor}`,
        borderRadius: '8px',
        boxShadow: topNavigationStyles.dropdownShadow,
        display: 'inline-flex',
        gap: '8px',
        left: 0,
        minHeight: '128px',
        mt: '8px',
        p: '12px',
        position: 'absolute',
        top: '100%',
        zIndex: 1000,
      })}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          width: '216px',
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          {menu.sections.map(section => {
            const sectionIsActive = activeSection?.key === section.key

            return (
              <Box
                component="button"
                key={section.key}
                onClick={() => onSectionChange(section.key)}
                sx={theme => ({
                  alignItems: 'center',
                  backgroundColor: sectionIsActive
                    ? alpha(theme.palette.primary.main, 0.04)
                    : 'transparent',
                  border: 0,
                  borderRadius: '4px',
                  color: theme.palette.text.primary,
                  cursor: 'pointer',
                  display: 'flex',
                  gap: '8px',
                  minHeight: '32px',
                  px: 0,
                  py: '4px',
                  textAlign: 'left',
                  width: '100%',
                })}
                type="button"
              >
                <Box
                  sx={{
                    alignItems: 'center',
                    display: 'inline-flex',
                    flexShrink: 0,
                    justifyContent: 'center',
                    width: '36px',
                  }}
                >
                  <DownChevron
                    sx={theme => ({ color: theme.palette.text.primary })}
                  />
                </Box>
                <Box
                  sx={{
                    alignItems: 'center',
                    display: 'flex',
                    flex: 1,
                    gap: '8px',
                    justifyContent: 'space-between',
                    minWidth: 0,
                  }}
                >
                  <Typography
                    component="span"
                    sx={{
                      color: 'inherit',
                      flex: 1,
                      fontSize: '14px',
                      fontWeight: 400,
                      letterSpacing: 0,
                      lineHeight: '20px',
                      minWidth: 0,
                    }}
                  >
                    {section.label}
                  </Typography>
                  {sectionIsActive ? (
                    <RightChevron
                      sx={theme => ({ color: theme.palette.text.primary })}
                    />
                  ) : null}
                </Box>
              </Box>
            )
          })}
        </Box>

        {menu.footerLink ? (
          <NavigationLink
            isActive={menu.footerLink.isActive}
            linkComponent={linkComponent}
            onClick={onNavigate}
            rel={menu.footerLink.rel}
            sx={theme => ({
              alignItems: 'center',
              borderRadius: '4px',
              color: theme.palette.primary.main,
              display: 'flex',
              justifyContent: 'space-between',
              minHeight: '32px',
              px: '5px',
              py: '4px',
              textAlign: 'left',
              width: '100%',
              '&:hover': {
                backgroundColor:
                  getHoverBackground(theme),
              },
            })}
            target={menu.footerLink.target}
            to={menu.footerLink.to}
          >
            <Box
              sx={{
                alignItems: 'center',
                display: 'flex',
                flex: 1,
                gap: '8px',
                minWidth: 0,
              }}
            >
              <GearIcon />
              <Typography
                component="span"
                sx={{
                  color: 'inherit',
                  flex: 1,
                  fontSize: '14px',
                  fontWeight: 600,
                  letterSpacing: '0.028px',
                  lineHeight: '20px',
                }}
              >
                {menu.footerLink.label}
              </Typography>
            </Box>
          </NavigationLink>
        ) : null}
      </Box>

      <Box
        sx={{
          alignSelf: 'stretch',
          backgroundColor: topNavigationStyles.borderColor,
          width: '1px',
        }}
      />

      {/* TODO: TEMP-TopNav is a stand-in until cwan-ux ships; this scroll cap is a
          bandaid for long dashboard lists. But will probably want to be a requested change from ux team. */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          maxHeight: '60vh',
          overflowX: 'hidden',
          overflowY: 'auto',
          py: '8px',
          width: '340px',
        }}
      >
        {activeSection?.items.map(menuItem => (
          <NavigationLink
            isActive={menuItem.isActive}
            key={menuItem.key}
            linkComponent={linkComponent}
            onClick={onNavigate}
            rel={menuItem.rel}
            sx={theme => ({
              alignItems: 'center',
              borderRadius: '4px',
              color: theme.palette.text.primary,
              display: 'flex',
              gap: '8px',
              minHeight: '32px',
              px: '5px',
              py: '4px',
              textAlign: 'left',
              width: '100%',
              '&:hover': {
                backgroundColor:
                  getHoverBackground(theme),
                color: theme.palette.primary.main,
              },
            })}
            target={menuItem.target}
            to={menuItem.to}
          >
            <Typography
              component="span"
              sx={{
                color: 'inherit',
                flex: 1,
                fontSize: '14px',
                fontWeight: menuItem.isActive ? 600 : 400,
                letterSpacing: 0,
                lineHeight: '20px',
                minWidth: 0,
              }}
            >
              {menuItem.label}
            </Typography>
          </NavigationLink>
        )) ?? null}
      </Box>
    </Box>
  )
}

const TopNavigationMenuItem = ({
  activeSectionKey,
  anyMenuOpen,
  isOpen,
  linkComponent,
  menu,
  onNavigate,
  onSectionChange,
  onToggle,
}: TopNavigationMenuItemProps): React.ReactElement => {
  // While a menu is open, only the open one highlights; the active menu greys out.
  const menuIsSelected = !menu.disabled && (isOpen || (menu.isActive && !anyMenuOpen))

  const button = (
    <Box
      aria-disabled={menu.disabled || undefined}
      aria-expanded={menu.disabled ? undefined : isOpen}
      aria-haspopup="menu"
      component="button"
      onClick={menu.disabled ? undefined : onToggle}
      sx={theme => ({
        ...getTopLevelItemSx(menuIsSelected)(theme),
        color: menu.disabled
          ? theme.palette.text.disabled
          : menuIsSelected
            ? theme.palette.primary.main
            : theme.palette.text.primary,
        ...(menu.disabled && {
          cursor: 'not-allowed',
          '&:hover': { backgroundColor: 'transparent' },
        }),
      })}
      type="button"
    >
      <TopNavigationRootLabel
        isSelected={menuIsSelected}
        label={menu.label}
      />
      <Box
        aria-hidden
        sx={{
          alignItems: 'center',
          color: 'inherit',
          display: 'inline-flex',
          fontSize: '10px',
          height: '16px',
          justifyContent: 'center',
          width: '16px',
        }}
      >
        <ExpandMoreIcon sx={{ fontSize: 16 }} />
      </Box>
    </Box>
  )

  return (
    <Box
      component="li"
      sx={{
        alignItems: 'center',
        display: 'flex',
        listStyle: 'none',
        position: 'relative',
      }}
    >
      {menu.disabled ? (
        <Tooltip title={menu.disabledTooltip ?? ''}>
          <span style={{ display: 'inline-flex' }}>{button}</span>
        </Tooltip>
      ) : (
        button
      )}

      {!menu.disabled && isOpen ? (
        <TopNavigationMenuPanel
          activeSectionKey={activeSectionKey}
          linkComponent={linkComponent}
          menu={menu}
          onNavigate={onNavigate}
          onSectionChange={onSectionChange}
        />
      ) : null}
    </Box>
  )
}

const TopNavigationRoot: React.FC<TopNavigationProps> = ({
  children,
  className,
  currentPath,
  linkComponent,
  utilityActions = [],
}) => {
  const rootRef = useRef<HTMLDivElement | null>(null)
  const { brand, items } = useMemo(
    () => parseTopNavigationChildren(children, currentPath),
    [children, currentPath],
  )
  const menus = useMemo(
    () => items.flatMap(item => (item.kind === 'menu' ? [item.menu] : [])),
    [items],
  )

  const [openMenuKey, setOpenMenuKey] = useState<string | null>(null)
  const [activeSectionKey, setActiveSectionKey] = useState<string | null>(null)

  const openMenu = menus.find(menu => menu.key === openMenuKey) ?? null
  const hasEndControls = utilityActions.length > 0

  useEffect(() => {
    if (openMenuKey === null) {
      return
    }

    if (!menus.some(menu => menu.key === openMenuKey)) {
      setOpenMenuKey(null)
    }
  }, [menus, openMenuKey])

  useEffect(() => {
    if (openMenu === null) {
      setActiveSectionKey(null)
      return
    }

    const defaultSectionKey = getDefaultSectionKey(openMenu)
    setActiveSectionKey(previousSectionKey => {
      if (
        previousSectionKey &&
        openMenu.sections.some(section => section.key === previousSectionKey)
      ) {
        return previousSectionKey
      }

      return defaultSectionKey
    })
  }, [openMenu])

  useEffect(() => {
    if (openMenu === null) {
      return
    }

    const handleDocumentMouseDown = (event: MouseEvent) => {
      const target = event.target
      if (!(target instanceof Node)) {
        return
      }

      if (rootRef.current && !rootRef.current.contains(target)) {
        setOpenMenuKey(null)
      }
    }

    document.addEventListener('mousedown', handleDocumentMouseDown)
    return () => {
      document.removeEventListener('mousedown', handleDocumentMouseDown)
    }
  }, [openMenu])

  const closeMenu = () => {
    setOpenMenuKey(null)
  }

  return (
    <Box
      className={className}
      ref={rootRef}
      sx={{
        alignItems: 'center',
        display: 'flex',
        gap: '24px',
        justifyContent: hasEndControls ? 'space-between' : 'flex-start',
        width: hasEndControls ? '100%' : 'fit-content',
      }}
    >
      <Box
        sx={theme => ({
          alignItems: 'center',
          backgroundColor:
            theme.palette.primary.dark ?? theme.palette.primary.main,
          border: `1px solid ${theme.palette.primary.dark ?? theme.palette.primary.main}`,
          borderRadius: '6px',
          boxShadow: topNavigationStyles.shellShadow,
          display: 'inline-flex',
          gap: 0,
          p: '1px',
        })}
      >
        {brand ? (
          <TopNavigationBrandLink
            brand={brand}
            hasTrailingRail={items.length > 0}
            linkComponent={linkComponent}
            onNavigate={closeMenu}
          />
        ) : null}

        <Box
          aria-label="Top navigation"
          component="nav"
          sx={theme => ({
            backgroundColor: theme.palette.background.paper,
            borderRadius: '6px',
            height: '44px',
            overflow: 'visible',
          })}
        >
          <Box
            component="ul"
            sx={{
              alignItems: 'center',
              display: 'flex',
              gap: '2px',
              height: '44px',
              listStyle: 'none',
              m: 0,
              p: 0,
            }}
          >
            {items.map(item =>
              item.kind === 'link' ? (
                <TopNavigationLeafItem
                  key={item.link.key}
                  link={item.link}
                  linkComponent={linkComponent}
                  onNavigate={closeMenu}
                />
              ) : (
                <TopNavigationMenuItem
                  activeSectionKey={activeSectionKey}
                  anyMenuOpen={openMenu !== null}
                  isOpen={openMenu?.key === item.menu.key}
                  key={item.menu.key}
                  linkComponent={linkComponent}
                  menu={item.menu}
                  onNavigate={closeMenu}
                  onSectionChange={setActiveSectionKey}
                  onToggle={() =>
                    setOpenMenuKey(previousMenuKey =>
                      previousMenuKey === item.menu.key ? null : item.menu.key,
                    )
                  }
                />
              ),
            )}
          </Box>
        </Box>
      </Box>

      {hasEndControls ? (
        <Box
          sx={{
            alignItems: 'center',
            display: 'flex',
            flexShrink: 0,
            gap: '8px',
          }}
        >
          {utilityActions.length > 0 ? (
            <Box
              sx={{
                alignItems: 'center',
                display: 'flex',
              }}
            >
              {utilityActions.map(action => (
                <TopNavigationUtilityActionButton
                  action={action}
                  key={action.ariaLabel}
                  onAction={closeMenu}
                />
              ))}
            </Box>
          ) : null}
        </Box>
      ) : null}
    </Box>
  )
}

interface TopNavigationCompoundComponent {
  (props: TopNavigationProps): React.ReactElement
  Brand: typeof TopNavigationBrandMarker
  FooterLink: typeof TopNavigationFooterLinkMarker
  Link: typeof TopNavigationLinkMarker
  Menu: typeof TopNavigationMenuMarker
  Section: typeof TopNavigationSectionMarker
}

export const TopNavigation = Object.assign(TopNavigationRoot, {
  Brand: TopNavigationBrandMarker,
  FooterLink: TopNavigationFooterLinkMarker,
  Link: TopNavigationLinkMarker,
  Menu: TopNavigationMenuMarker,
  Section: TopNavigationSectionMarker,
}) as TopNavigationCompoundComponent
